import { GuidService } from 'ui-components/services/guid-service';
import { buildPackageWithPreferredUnits } from '../../../../services/default-package-service';
import { defaultGenericPackage } from '../../../../services/package-service';
import { convertPackageWeightAndDimensions } from '../../../../services/unit-services/package-services/package-unit-conversion-service';
import type { Package } from '../types';
import type { ItemQuantities } from './assignment-helpers';
import type { InsuredValue } from '../../multi-package-modal/package-set';

export const addInitialPackageIds = <T extends Record<string, unknown>>(pkg: T): T & { packageTypeId: string; packageId: string } => {
  if ('packageTypeId' in pkg && pkg.packageTypeId) {
    return pkg as T & { packageTypeId: string; packageId: string };
  }
  return {
    ...pkg,
    packageTypeId: defaultGenericPackage.packageTypeId,
    packageId: defaultGenericPackage.packageId,
  };
};

export const createInitialPackages = (
  initialPackages: Partial<Package>[],
  sellerDefaultCurrencyCode: string,
  sellerDefaultDimensionsUnit: string,
  sellerDefaultWeightUnit: string,
  hasInsuranceProviderSelected: boolean,
): Package[] => {
  const defaultPackageCurrencyCode = initialPackages[0]?.insuredValue?.code || sellerDefaultCurrencyCode;
  const packageToAdd = buildPackageWithPreferredUnits(
    addInitialPackageIds(initialPackages[0] ?? {}),
    sellerDefaultWeightUnit,
    sellerDefaultDimensionsUnit,
    defaultPackageCurrencyCode,
  );

  const packagesToProcess =
    initialPackages.length > 1
      ? initialPackages
      : [
          { ...packageToAdd, orderPackageId: null },
          { ...packageToAdd, assignedItems: [], orderPackageId: null },
        ];

  const calculatedPackages = packagesToProcess.map((p: Partial<Package>) => {
    const converted = convertPackageWeightAndDimensions(p, sellerDefaultWeightUnit, sellerDefaultDimensionsUnit);
    const insuredValue: InsuredValue =
      hasInsuranceProviderSelected && p.insuredValue ? p.insuredValue : { value: 0, code: defaultPackageCurrencyCode };

    return {
      ...converted,
      insuredValue,
      contentDescription: p.contentDescription ?? undefined,
      id: GuidService.newGuid(),
    } as Package;
  });

  return calculatedPackages;
};

export const createDefaultPackage = (
  sellerDefaultWeightUnit: string,
  sellerDefaultDimensionsUnit: string,
  currencyCode: string,
): Package => {
  const defaultPackage = buildPackageWithPreferredUnits(
    addInitialPackageIds({}),
    sellerDefaultWeightUnit,
    sellerDefaultDimensionsUnit,
    currencyCode,
  );
  return {
    ...defaultPackage,
    id: GuidService.newGuid(),
  } as Package;
};

const isPackageBlank = (pkg: Package): boolean =>
  !pkg.weight?.value && !pkg.dimensions?.width && !pkg.dimensions?.height && !pkg.dimensions?.length && !pkg.insuredValue?.value;

export const areTargetPackagesBlank = (currentPackages: Package[], incomingCount: number): boolean =>
  currentPackages.slice(0, incomingCount).every(isPackageBlank);

export const hasEmptyPackages = (packages: Package[], itemQuantities: ItemQuantities): boolean => {
  return packages.some((_, packageIndex) => {
    const hasAssignedItems = Object.values(itemQuantities).some((quantities) =>
      quantities.chunks.some((chunk) => chunk.packageIndex === packageIndex),
    );
    return !hasAssignedItems;
  });
};
